import { ChangeDetectionStrategy, Component, inject, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { take } from 'rxjs';
import { AppRoutes, CopyValueToClipboardDirective, MODAL_TOKEN, ModalProvider } from '@core';
import { OrAvatarComponent, OrIconComponent } from '@ui-components';
import {
  AdminUserQueryResult,
  GetAdminUsersListQueryHandler,
  PatchAdminUsersFiltersCommandHandler
} from '../../../application/handlers';
import { ModerateUserComponent } from '../moderate-user/moderate-user.component';
import { OpenSingleChatCommandHandler } from '@messages';
import { ChatService } from '@messages/infra';

@Component({
  selector: 'lib-users-list',
  templateUrl: './users-list.component.html',
  styleUrls: ['./users-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CopyValueToClipboardDirective, OrAvatarComponent, OrIconComponent],
  providers: [GetAdminUsersListQueryHandler, OpenSingleChatCommandHandler, ChatService]
})
export class UsersListComponent {
  private readonly getAdminUsersListQueryHandler: GetAdminUsersListQueryHandler = inject(GetAdminUsersListQueryHandler);
  private readonly openSingleChatCommandHandler: OpenSingleChatCommandHandler = inject(OpenSingleChatCommandHandler);
  private readonly modalProvider: ModalProvider = inject(MODAL_TOKEN);
  private readonly patchAdminUsersFiltersCommandHandler: PatchAdminUsersFiltersCommandHandler = inject(
    PatchAdminUsersFiltersCommandHandler
  );
  private readonly router: Router = inject(Router);

  readonly users: Signal<AdminUserQueryResult[] | undefined> = toSignal(this.getAdminUsersListQueryHandler.query());

  moderateUser(user: AdminUserQueryResult, event: Event): void {
    event.stopImmediatePropagation();

    this.modalProvider
      .showModal$({
        component: ModerateUserComponent,
        componentProps: { user },
        cssClass: 'modal-auto',
        initialBreakpoint: 1,
        breakpoints: [0, 1]
      })
      .pipe(take(1))
      .subscribe(() => this.patchAdminUsersFiltersCommandHandler.patch({}).pipe(take(1)).subscribe());
  }

  sendMessage(user: AdminUserQueryResult, event: Event): void {
    event.stopImmediatePropagation();
    const displayName: string = `${user.name} ${user.lastName}`.trim();
    this.openSingleChatCommandHandler.open(user.userId, displayName).subscribe();
  }

  goToProfile(userId: string): void {
    this.router.navigate([`${AppRoutes.PROFILE}/${userId}`]);
  }
}
